import { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  PageContainer,
  Alert,
  Button,
  Select,
  Input,
  DataTable,
  Skeleton,
  DatePicker,
  EmptyState,
  ErrorBoundary,
  useToast,
} from '@bbpos/ui';
import type { ColumnDef, DateRange } from '@bbpos/ui';
import { GLASS, GLASS_FILTER } from '@/constants/design-tokens';
import { useRfqs, useRfqStats, RfqStatusBadge } from '@/features/rfqs';
import type { RfqListItem, RfqStatus } from '@/features/rfqs';
import { PoKpi } from '@/features/purchase-orders';
import { useDashboardStore } from '@/stores/use-dashboard-store';
import { useAuthStore } from '@/stores/use-auth-store';
import { ROLE_LEVELS } from '@/types/user';
import { locationService } from '@/services/location.service';
import { useQuery } from '@tanstack/react-query';
import { toLocalIsoDate } from '../purchase-orders/date-utils';
import { ClipboardIcon, SendIcon, CheckCircleIcon, ClockIcon, PlusIcon, EyeIcon } from '../purchase-orders/icons';

const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'DRAFT', label: 'Draft' },
  { value: 'SENT', label: 'Sent' },
  { value: 'CLOSED', label: 'Closed' },
  { value: 'CANCELLED', label: 'Cancelled' },
];

const ALL_LOCATIONS = 'all';

function fmtDate(value: string | null) {
  return value
    ? new Date(value).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
    : '—';
}

function RfqListHeader({ isManager, onNew }: { isManager: boolean; onNew: () => void }) {
  return (
    <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-teal-600 via-teal-500 to-brand-600 p-6 text-white shadow-[0_24px_60px_-24px_rgba(13,148,136,0.55)] mb-6 animate-fade-in">
      <div className="pointer-events-none absolute -top-24 -right-24 h-56 w-56 rounded-full bg-white/20 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-20 -left-10 h-48 w-48 rounded-full bg-black/10 blur-3xl" />
      <div className="relative flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-white">Requests for Quotation</h1>
          <p className="text-teal-100 mt-1">Ask several suppliers to quote, compare, then raise purchase orders</p>
        </div>
        {isManager && (
          <Button variant="secondary" onClick={onNew} className="bg-white/95 text-teal-700 hover:bg-white">
            <PlusIcon className="h-4 w-4 mr-1.5" />
            New RFQ
          </Button>
        )}
      </div>
    </div>
  );
}

function RfqListContent() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { toast } = useToast();
  const selectedLocationId = useDashboardStore((s) => s.selectedLocationId);
  const profile = useAuthStore((s) => s.profile);

  const page = Number(searchParams.get('page') ?? '1');
  const status = searchParams.get('status') ?? '';
  const search = searchParams.get('search') ?? '';
  const locationParam = searchParams.get('location_id') ?? '';
  const from = searchParams.get('from') ?? '';
  const to = searchParams.get('to') ?? '';
  const effectiveLocationId = locationParam === ALL_LOCATIONS ? undefined : locationParam || selectedLocationId;
  const dateRange: DateRange | undefined = from && to ? { from: new Date(from), to: new Date(to) } : undefined;

  const roleLevel = ROLE_LEVELS[profile?.role ?? ''] ?? 0;
  const isManager = roleLevel >= ROLE_LEVELS.MANAGER;

  const { data: locationsData } = useQuery({
    queryKey: ['locations-list'],
    queryFn: () => locationService.list({ limit: 100, is_active: 'true' }),
    staleTime: 60_000,
  });
  const locations = locationsData?.data ?? [];

  const [searchInput, setSearchInput] = useState(search);
  const searchTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const searchFieldRef = useRef<HTMLInputElement>(null);

  const handleSearch = useCallback(
    (value: string) => {
      setSearchInput(value);
      clearTimeout(searchTimerRef.current);
      searchTimerRef.current = setTimeout(() => {
        setSearchParams((prev) => {
          const next = new URLSearchParams(prev);
          if (value) next.set('search', value); else next.delete('search');
          next.set('page', '1');
          return next;
        });
      }, 300);
    },
    [setSearchParams],
  );

  function setParam(key: string, value: string) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value) next.set(key, value); else next.delete(key);
      next.set('page', '1');
      return next;
    });
  }
  function setPage(p: number) {
    setSearchParams((prev) => { const next = new URLSearchParams(prev); next.set('page', String(p)); return next; });
  }
  function setDateRange(range: DateRange | undefined) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (range?.from && range?.to) { next.set('from', toLocalIsoDate(range.from)); next.set('to', toLocalIsoDate(range.to)); }
      else { next.delete('from'); next.delete('to'); }
      next.set('page', '1');
      return next;
    });
  }

  const { data, isLoading, isError, error, refetch } = useRfqs({
    page, limit: 20,
    status: (status as RfqStatus) || undefined,
    search: search || undefined,
    start_date: from || undefined,
    end_date: to || undefined,
    location_id: effectiveLocationId,
  });
  const { data: stats } = useRfqStats({ location_id: effectiveLocationId });

  useEffect(() => {
    if (error) toast({ title: (error as Error).message, variant: 'error' });
  }, [error]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      if (e.key === '/') { e.preventDefault(); searchFieldRef.current?.focus(); }
      if (e.key === 'n' && isManager) { e.preventDefault(); navigate('/inventory/rfqs/new'); }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [navigate, isManager]);

  const rfqs = data?.data ?? [];
  const total = data?.meta?.total ?? 0;

  const columns: ColumnDef<RfqListItem>[] = [
    {
      key: 'rfq_number', header: 'RFQ #',
      render: (_v, row) => (
        <button type="button" onClick={() => navigate(`/inventory/rfqs/${row.id}`)}
          className="text-sm text-teal-600 dark:text-teal-400 hover:underline tabular-nums text-left font-medium">
          {row.rfq_number}
        </button>
      ),
    },
    { key: 'location_name', header: 'Location', render: (_v, row) => <span className="text-sm text-neutral-500 dark:text-neutral-400">{row.location_name}</span> },
    { key: 'item_count', header: 'Items', numeric: true, render: (_v, row) => <span className="text-sm tabular-nums text-neutral-700 dark:text-neutral-300">{row.item_count}</span> },
    {
      key: 'supplier_count', header: 'Suppliers', numeric: true,
      render: (_v, row) => (
        <span className="text-sm tabular-nums text-neutral-700 dark:text-neutral-300">
          {row.supplier_count}
          <span className="text-xs text-neutral-500"> · {row.quoted_count} quoted</span>
        </span>
      ),
    },
    { key: 'po_count', header: 'POs', numeric: true, render: (_v, row) => <span className="text-sm tabular-nums text-neutral-700 dark:text-neutral-300">{row.po_count}</span> },
    { key: 'status', header: 'Status', render: (_v, row) => <RfqStatusBadge status={row.status} /> },
    { key: 'expected_delivery_date', header: 'Required by', render: (_v, row) => <span className="text-sm tabular-nums text-neutral-600 dark:text-neutral-400">{fmtDate(row.expected_delivery_date)}</span> },
    { key: 'created_at', header: 'Created', render: (_v, row) => <span className="text-sm tabular-nums text-neutral-600 dark:text-neutral-400">{fmtDate(row.created_at)}</span> },
    {
      key: 'actions', header: '',
      render: (_v, row) => (
        <button type="button" onClick={() => navigate(`/inventory/rfqs/${row.id}`)} title="View"
          className="p-1.5 rounded-lg text-neutral-500 hover:text-teal-600 hover:bg-teal-50 dark:hover:bg-teal-900/20 transition-colors">
          <EyeIcon className="h-4 w-4" />
        </button>
      ),
    },
  ];

  if (isLoading && !data) {
    return (
      <>
        <Skeleton variant="rect" height={96} className="rounded-xl mb-6" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6 mb-6">
          {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} variant="rect" height={120} className="rounded-xl" />)}
        </div>
        <Skeleton variant="rect" height={56} className="rounded-xl mb-4" />
        <Skeleton variant="rect" height={400} className="rounded-xl" />
      </>
    );
  }

  // Failure MUST render before the empty state — a dead endpoint leaves total at 0 too.
  if (isError) {
    return (
      <>
        <RfqListHeader isManager={isManager} onNew={() => navigate('/inventory/rfqs/new')} />
        <Alert
          variant="danger"
          title="Couldn't load RFQs"
          description={(error as Error)?.message || 'The RFQ list request failed — this is a server problem, not an empty list.'}
          action={{ label: 'Retry', onClick: () => void refetch() }}
        />
      </>
    );
  }

  if (total === 0 && !status && !search && !from && !locationParam) {
    return (
      <>
        <RfqListHeader isManager={isManager} onNew={() => navigate('/inventory/rfqs/new')} />
        <div className={`${GLASS} p-10`}>
          <EmptyState
            title="No RFQs yet"
            description="Create a request for quotation, send it to your suppliers and compare their prices before raising a purchase order."
            action={isManager ? { label: 'New RFQ', onClick: () => navigate('/inventory/rfqs/new') } : undefined}
          />
        </div>
      </>
    );
  }

  return (
    <>
      <RfqListHeader isManager={isManager} onNew={() => navigate('/inventory/rfqs/new')} />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6 mb-6">
        <PoKpi title="Total RFQs" value={(stats?.total ?? 0).toLocaleString('en-IN')} tone="teal" icon={<ClipboardIcon className="h-5 w-5" />} delay={0} />
        <PoKpi title="Draft" value={(stats?.by_status?.DRAFT ?? 0).toLocaleString('en-IN')} tone="warning" icon={<ClockIcon className="h-5 w-5" />} delay={60} />
        <PoKpi title="Awaiting quotes" value={(stats?.by_status?.SENT ?? 0).toLocaleString('en-IN')} tone="brand" icon={<SendIcon className="h-5 w-5" />} delay={120} />
        <PoKpi title="Closed" value={(stats?.by_status?.CLOSED ?? 0).toLocaleString('en-IN')} tone="success" icon={<CheckCircleIcon className="h-5 w-5" />} delay={180} />
      </div>

      <div className={`${GLASS_FILTER} flex flex-wrap items-end gap-3 mb-4 p-3`}>
        <div className="w-64">
          <DatePicker variant="range" value={dateRange} onChange={(r) => setDateRange(r as DateRange | undefined)} placeholder="Filter by date range" />
        </div>
        <div className="w-44">
          <Select options={STATUS_OPTIONS} value={status} onChange={(v) => setParam('status', typeof v === 'string' ? v : '')} placeholder="All Statuses" />
        </div>
        <div className="w-56">
          <Input ref={searchFieldRef} placeholder="Search RFQ # or supplier..." value={searchInput} onChange={(v) => handleSearch(v)} />
        </div>
        {locations.length > 1 && (
          <div className="w-48">
            <Select
              options={[{ value: '', label: 'All Locations' }, ...locations.map((l) => ({ value: l.id, label: l.name }))]}
              value={effectiveLocationId ?? ''}
              onChange={(v) => setParam('location_id', (typeof v === 'string' && v) || ALL_LOCATIONS)}
              placeholder="All Locations"
            />
          </div>
        )}
      </div>

      <div className={`${GLASS} animate-slide-up`}>
        <DataTable<RfqListItem>
          columns={columns}
          data={rfqs}
          loading={isLoading}
          totalCount={total}
          currentPage={page}
          pageSize={20}
          onPageChange={setPage}
          emptyState={{ title: 'No RFQs match filters', description: 'Try adjusting your filters or date range' }}
        />
      </div>
    </>
  );
}

export function RfqListPage() {
  return (
    <PageContainer title="RFQs" breadcrumbs={[{ label: 'Inventory', href: '/inventory' }, { label: 'RFQs' }]}>
      <ErrorBoundary>
        <RfqListContent />
      </ErrorBoundary>
    </PageContainer>
  );
}
